import {
    type ChangeEventHandler,
    type ComponentRef,
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import '../../../../css/forms.css';
import '../../../../css/tenant-name-input.less';
import { SUBTENANT_PARENT_NAME_SEPARATOR, TENANT_NAME_PREFIX } from '../../constants/tenant';
import { TENANT_NAME_MAX_LENGTH } from '../../constants/validation';
import translations from '../../translations';
import { getTenantNameValidityMessage } from '../../utils/validation';
import FormGroup from '../generic/FormGroup';

interface Props {
    label: string;
    value: string;
    parentTenantName?: string;
    showValidation: boolean;
    isRequired?: boolean;
    restrictionsText?: string;
    onChange: ({ value, isValid }: { value: string; isValid: boolean }) => void;
}

const TenantNameInput = ({
    value,
    label,
    parentTenantName = '',
    showValidation,
    isRequired = true,
    restrictionsText = translations.TENANT_naming_restrictions_text,
    onChange,
}: Props) => {
    const [validityMessage, setValidityMessage] = useState('');
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const inputRef = useRef<ComponentRef<'input'>>(null);
    const inputId = useId();

    const validateInput = useCallback(
        (value: string) => {
            const message = getTenantNameValidityMessage(value);
            inputRef.current?.setCustomValidity(message);
            setValidityMessage(message);

            const isValid = message.length === 0;
            setIsValid(isValid);

            onChange({ value, isValid });
        },
        [onChange],
    );

    // Ensure empty state is validated
    useEffect(() => {
        if (isValid === null) {
            validateInput(value);
        }
    }, [isValid, value, validateInput]);

    const handleChange: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
        validateInput(value);
    };

    const formattedParentTenantName = parentTenantName.replace(TENANT_NAME_PREFIX, '');

    return (
        <FormGroup
            label={label}
            htmlFor={inputId}
            isValid={!!isValid}
            validationMessage={validityMessage}
            showValidation={showValidation}
            isRequired={isRequired}
            className="tenant-name-component-container"
        >
            <div className="tenant-name-input-addons-container">
                <input
                    id={inputId}
                    className="form-control tenant-name-input-field"
                    maxLength={TENANT_NAME_MAX_LENGTH}
                    required={isRequired}
                    value={value}
                    onChange={handleChange}
                    ref={inputRef}
                />
                {parentTenantName ? (
                    <span
                        data-testid="subtenant-name-suffix"
                        className="tenant-name-input-addon subtenant-name-suffix"
                    >
                        {`${SUBTENANT_PARENT_NAME_SEPARATOR}${formattedParentTenantName}`}
                    </span>
                ) : null}
            </div>
            <span className="help-block">{restrictionsText}</span>
        </FormGroup>
    );
};

export default TenantNameInput;
